import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Grid, Divider,
  Alert, CircularProgress, Stack,
} from '@mui/material';
import { Save } from '@mui/icons-material';
import { adminAPI } from '../services/api';
import { clearCompanyInfoCache } from '../services/companyInfo';

const FIELDS = [
  { key: 'company_name',    label: '회사명' },
  { key: 'order_prefix',    label: '주문번호 머릿글', placeholder: '나라  (예: 나라-ORD-20260511-0001)' },
  { key: 'ceo_name',        label: '대표자명' },
  { key: 'company_phone',   label: '회사 대표 연락처', placeholder: '010-2370-9760' },
  { key: 'business_number', label: '사업자등록번호',  placeholder: '306-31-96499' },
  { key: 'company_address', label: '회사 주소', fullWidth: true },
];
const BANK_FIELDS = [
  { key: 'bank_name',       label: '은행명' },
  { key: 'account_number',  label: '계좌번호' },
  { key: 'account_holder',  label: '예금주' },
];
const DISPATCH_FIELDS = [
  {
    key: 'broadcast_response_seconds', label: '선착순 응답 시간 (초)', placeholder: '60', type: 'number',
    helper: '기사에게 수락/포기 응답을 기다리는 시간. 20~600초, 비우면 기본 60초(1분)',
  },
  {
    key: 'broadcast_grace_seconds', label: '신청자 소진 시 유예 시간 (초)', placeholder: '180', type: 'number',
    helper: '신청자가 모두 포기/무응답일 때 배차실패 처리 전 대기 시간. 0~3600초, 비우면 기본 180초(3분)',
  },
];

function Settings() {
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null); // {type, text}

  useEffect(() => {
    (async () => {
      try {
        const res = await adminAPI.getSettings();
        setForm(res.data?.data || {});
      } catch (err) {
        setMsg({ type: 'error', text: '설정을 불러오지 못했습니다.' });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleChange = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSave = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const payload = {};
      [...FIELDS, ...BANK_FIELDS, ...DISPATCH_FIELDS].forEach((f) => {
        payload[f.key] = form[f.key] ?? '';
      });
      await adminAPI.updateSettings(payload);
      clearCompanyInfoCache();
      setMsg({ type: 'success', text: '저장되었습니다.' });
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || '저장에 실패했습니다.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>;
  }

  const renderField = (f) => (
    <Grid item xs={12} md={f.fullWidth ? 12 : 6} key={f.key}>
      <TextField
        fullWidth
        label={f.label}
        placeholder={f.placeholder}
        type={f.type || 'text'}
        helperText={f.helper}
        value={form[f.key] ?? ''}
        onChange={handleChange(f.key)}
      />
    </Grid>
  );

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>시스템 설정</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        여기서 설정한 값은 고객 포털, 직원 포털, 운전자 앱, 개인정보처리방침/이용약관 등에 공통으로 적용됩니다.
      </Typography>

      {msg && (
        <Alert severity={msg.type} sx={{ mb: 2 }} onClose={() => setMsg(null)}>
          {msg.text}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>회사 정보</Typography>
        <Grid container spacing={2}>
          {FIELDS.map(renderField)}
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>입금 계좌</Typography>
        <Grid container spacing={2}>
          {BANK_FIELDS.map(renderField)}
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>배차 호출 (선착순 타이머)</Typography>
        <Grid container spacing={2}>
          {DISPATCH_FIELDS.map(renderField)}
        </Grid>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          변경 사항은 저장 후 새로 시작되는 호출부터 적용됩니다 (최대 30초 지연).
          허용 범위를 벗어난 값은 기본값으로 동작합니다.
        </Typography>
      </Paper>

      <Divider sx={{ my: 2 }} />
      <Stack direction="row" justifyContent="flex-end">
        <Button
          variant="contained"
          size="large"
          startIcon={<Save />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? '저장 중…' : '저장'}
        </Button>
      </Stack>
    </Box>
  );
}

export default Settings;
